import { z } from 'zod';
/**
 * Результат калькулятора на лендинге, который переносится в аккаунт.
 *
 * Раньше в приложение уезжали только budget/area/city в query-параметрах, и
 * человек попадал в пустую форму проекта: смету приходилось набивать заново.
 * Теперь переносится и разбивка по категориям — проект создаётся сразу с
 * заполненным планом бюджета.
 */
export const calculatorHandoffLineSchema = z.object({
    category: z.string().min(1).max(100),
    amount: z.number().nonnegative(),
});
export const calculatorHandoffSchema = z.object({
    /** Версия формата: старые ссылки не должны ломать новый парсер. */
    v: z.literal(1),
    city: z.string().max(32).nullable().optional(),
    cityName: z.string().max(120).nullable().optional(),
    area: z.number().nonnegative(),
    budget: z.number().nonnegative(),
    pricePerSqm: z.number().nonnegative().optional(),
    lines: z.array(calculatorHandoffLineSchema).max(40).default([]),
});
/** Имя query-параметра, в котором payload едет с лендинга в webapp. */
export const CALCULATOR_HANDOFF_PARAM = 'fromCalculator';
// ─── base64url для URL-safe передачи ──────────────────────────────────────────
function bytesToBase64(bytes) {
    let binary = '';
    // По одному символу, а не спредом: спред большого массива роняет стек.
    for (let i = 0; i < bytes.length; i++)
        binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}
function base64ToBytes(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
/** Кодирует payload в компактную URL-safe строку. */
export function encodeCalculatorHandoff(data) {
    const wire = {
        v: 1,
        c: data.city ?? null,
        n: data.cityName ?? null,
        a: data.area,
        b: data.budget,
        p: data.pricePerSqm,
        l: data.lines.map((line) => [line.category, line.amount]),
    };
    const bytes = new TextEncoder().encode(JSON.stringify(wire));
    return bytesToBase64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
/**
 * Разбирает payload из URL. Возвращает null на любой мусор — битая ссылка не
 * должна ломать вход в приложение, человек просто увидит пустую форму.
 */
export function decodeCalculatorHandoff(raw) {
    if (!raw)
        return null;
    try {
        const base64 = raw.replace(/-/g, '+').replace(/_/g, '/');
        const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
        const json = new TextDecoder().decode(base64ToBytes(padded));
        const wire = JSON.parse(json);
        const parsed = calculatorHandoffSchema.safeParse({
            v: wire?.v,
            city: wire?.c ?? null,
            cityName: wire?.n ?? null,
            area: wire?.a,
            budget: wire?.b,
            pricePerSqm: wire?.p,
            lines: Array.isArray(wire?.l)
                ? wire.l.map(([category, amount]) => ({ category, amount }))
                : [],
        });
        return parsed.success ? parsed.data : null;
    }
    catch {
        return null;
    }
}
//# sourceMappingURL=calculator-handoff.js.map